import { Input } from '../../../shared/components/Input';
import { PriceInput } from '../../../shared/components/PriceInput';
import Button from '../../../shared/components/Button';
import { parsePriceToKobo, koboToInputValue } from '../../../shared/utils/priceUtils';
import { emptyWeightTier, type WeightTierFormValues } from '../payload';

interface Props {
  tiers: WeightTierFormValues[];
  onChange: (tiers: WeightTierFormValues[]) => void;
}

/**
 * Editable weight-based rate tiers (PE-2172/PE-2173). Ascending, non-overlapping bands;
 * only the last row may leave "up to (g)" blank, meaning "and above" — see payload.ts
 * for the validation this UI is built around.
 */
export function WeightTierRows({ tiers, onChange }: Props) {
  const update = (index: number, patch: Partial<WeightTierFormValues>) =>
    onChange(tiers.map((t, i) => (i === index ? { ...t, ...patch } : t)));

  const addTier = () => onChange([...tiers, emptyWeightTier()]);

  const removeTier = (index: number) => onChange(tiers.filter((_, i) => i !== index));

  return (
    <div className="space-y-3">
      <p className="text-[12px] text-muted-foreground bg-muted rounded-lg px-3 py-2">
        Weight tiers are currently evaluated against a fixed 500 g cart weight (backend PE-2277) —
        only the tier covering 500 g will apply until per-item weights ship.
      </p>

      {tiers.map((tier, i) => {
        const isLast = i === tiers.length - 1;
        return (
          <div key={i} className="flex items-end gap-3">
            <div className="w-[180px]">
              <Input
                label={i === 0 ? 'Up to (g)' : undefined}
                type="number"
                min={1}
                placeholder={isLast ? 'and above' : 'e.g. 1000'}
                value={tier.maxWeightGrams}
                onChange={(e) => update(i, { maxWeightGrams: e.target.value })}
              />
            </div>
            <div className="w-[180px]">
              <PriceInput
                label={i === 0 ? 'Price' : undefined}
                value={parsePriceToKobo(tier.amountNaira)}
                onChange={(kobo) => update(i, { amountNaira: koboToInputValue(kobo) })}
              />
            </div>
            {isLast && (
              <span className="text-[12px] text-muted-foreground pb-2">
                {tier.maxWeightGrams ? '' : '(and above)'}
              </span>
            )}
            {tiers.length > 1 && (
              <button
                type="button"
                onClick={() => removeTier(i)}
                className="text-xs text-red-500 hover:underline pb-2"
              >
                Remove
              </button>
            )}
          </div>
        );
      })}

      <Button type="button" variant="outline" size="sm" className="h-[36px]" onClick={addTier}>
        Add tier
      </Button>
    </div>
  );
}
